import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DashboardService } from '../services/dashboard.service';
import { ArticlesData } from '../model/article.type';
import { Brand } from '../model/brand.type';
import { DashboardConfig, DashboardItemType } from '../model/dashboard.type';
import { Journey } from '../model/journey.type';
import { Planning } from '../model/planning.type';
import { TasksData } from '../model/tasks.type';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { CountersService } from '@app/core/services/counters.service';
import { userFirstNameSelector } from '@app/profile/store/profile.slice';
import { JobsWidget, JobSearchWidget } from '../model/jobs.type';
import { CelebrationService } from '../services/celebration.service';

@Injectable({ providedIn: 'root' })
export class DashboardSandbox {
  private readonly dashboardService = inject(DashboardService);
  private readonly countersService = inject(CountersService);
  private readonly store = inject(Store);
  private readonly celebrationService = inject(CelebrationService);

  dashboardConfig = signal<DashboardConfig | null>(null);
  brand = signal<Brand | null>(null);
  planning = signal<Planning | null>(null);
  tasksData = signal<TasksData | null>(null);
  articlesData = signal<ArticlesData | null>(null);
  journey = signal<Journey | null>(null);
  jobSearchWidget = signal<JobSearchWidget | null>(null);
  jobsWidget = signal<JobsWidget | null>(null);

  readonly userFirstName = toSignal(this.store.select(userFirstNameSelector), { initialValue: null });
  readonly inboxCount = this.countersService.numberOfUnreadInboxMessages;
  readonly pendingCelebration = this.celebrationService.pendingCelebration;

  private onResumeListener: PluginListenerHandle;

  fetchConfig(): Observable<DashboardConfig> {
    return this.dashboardService.getConfig().pipe(tap((config) => this.dashboardConfig.set(config)));
  }

  fetchBrand(): Observable<Brand> {
    return this.dashboardService.getBrand().pipe(tap((config) => this.brand.set(config)));
  }

  fetchPlanning(): void {
    this.dashboardService.getPlanning().subscribe((planning) => this.planning.set(planning));
  }

  fetchTasksData(): void {
    this.dashboardService.getTasks().subscribe((tasks) => this.tasksData.set(tasks));
  }

  fetchArticles(): void {
    this.dashboardService.getArticles().subscribe((articles) => this.articlesData.set(articles));
  }

  fetchJourney(): void {
    this.dashboardService.getJourney().subscribe((journey) => this.journey.set(journey));
  }

  fetchJobSearchWidget(): void {
    this.dashboardService.getJobSearchWidget().subscribe((jobSearchWidget) => this.jobSearchWidget.set(jobSearchWidget));
  }

  fetchJobsWidget(): void {
    this.dashboardService.getJobsWidget().subscribe((jobsWidget) => this.jobsWidget.set(jobsWidget));
  }

  refreshWidget(type: DashboardItemType): void {
    switch (type) {
      case DashboardItemType.Tasks:
        this.fetchTasksData();
        break;
      case DashboardItemType.Planning:
        this.fetchPlanning();
        break;
      case DashboardItemType.Journey:
        this.fetchJourney();
        break;
      case DashboardItemType.Articles:
        this.fetchArticles();
        break;
      case DashboardItemType.JobSearch:
        this.fetchJobSearchWidget();
        break;
      case DashboardItemType.Jobs:
        this.fetchJobsWidget();
        break;
    }
  }

  refreshFullDashboard(): void {
    this.fetchConfig().subscribe((config: DashboardConfig) => {
      const widgetTypes = config.items.map((item) => item.type);
      if (widgetTypes.includes(DashboardItemType.Journey)) {
        this.fetchJourney();
      }
      if (widgetTypes.includes(DashboardItemType.Tasks)) {
        this.fetchTasksData();
      }
      if (widgetTypes.includes(DashboardItemType.Planning)) {
        this.fetchPlanning();
      }
      if (widgetTypes.includes(DashboardItemType.Articles)) {
        this.fetchArticles();
      }
      if (widgetTypes.includes(DashboardItemType.JobSearch)) {
        this.fetchJobSearchWidget();
      }
      if (widgetTypes.includes(DashboardItemType.Jobs)) {
        this.fetchJobsWidget();
      }
    });
    this.fetchBrand();
  }

  fetchCelebration(): void {
    this.celebrationService.refresh();
  }

  closeCelebration(id: string): void {
    this.celebrationService.close(id);
  }

  async setOnResumeListener(): Promise<void> {
    this.onResumeListener = await App.addListener('resume', () => {
      this.refreshFullDashboard();
      this.fetchCelebration();
    });
  }

  cleanUpListeners(): void {
    if (this.onResumeListener) {
      this.onResumeListener.remove();
      this.onResumeListener = null;
    }
    this.celebrationService.clear();
  }
}
